import { z, ZodSchema } from "zod";

import { DatasetId } from "./base";

const MetricsSchema = z.object({
  VARIATION_NAME: z.string().nullish(),
  label: z.string().nullish(),
});

export const MetricSchema = z.record(
  z.string(),
  z.union([z.string(), z.number()])
);
export type MetricType = z.infer<typeof MetricSchema>;

export const MetricArraySchema = MetricSchema.array().default([]);
export type MetricArrayType = z.infer<typeof MetricArraySchema>;

export const ExperimentMetricsSchema = z.object({
  TEST_GROUP_NAME: z.string().nullish(),
  VISITORS: z.number().nullish(),
  SITE_ORDERS: z.number().nullish(),
  SITE_CVR: z.number().nullish(),
  SITE_CVR_ERROR: z.number().nullish(),
  SITE_AOV: z.number().nullish(),
  SITE_AOV_ERR: z.number().nullish(),
  SITE_REVENUE_PER_VISITOR: z.number().nullish(),
  SITE_REVENUE_TOTAL: z.number().nullish(),
  AVG_SHIPPING_REV: z.number().nullish(),
  PERCENT_FREE_SHIPPING: z.number().nullish(),
  TEST_ORDERS: z.number().nullish(),
  TEST_CVR: z.number().nullish(),
  TEST_CVR_ERROR: z.number().nullish(),
  TEST_AOV: z.number().nullish(),
  TEST_AOV_ERR: z.number().nullish(),
  TEST_REVENUE_PER_VISITOR: z.number().nullish(),
  TEST_REVENUE_TOTAL: z.number().nullish(),
  TEST_PROFIT_PER_VISITOR: z.number().nullish(),
  TEST_PROFIT_TOTAL: z.number().nullish(),
  TEST_PRODUCT_REVENUE_AVG_PER_ORDER: z.number().nullish(),
  TEST_PRODUCT_REVENUE_TOTAL: z.number().nullish(),
  TEST_PRODUCT_REVENUE_PER_VISITOR: z.number().nullish(),
  TEST_PRODUCT_QUANTITY_AVG_PER_ORDER: z.number().nullish(),
  TEST_PRODUCT_QUANTITY_TOTAL: z.number().nullish(),
  ADD_TO_CART_RATE: z.number().nullish(),
  CHECKOUT_BEGIN_RATE: z.number().nullish(),
  A_VISITORS: z.number().nullish(),
  ADD_TO_CARTS: z.number().nullish(),
  CHECKOUT_BEGINS: z.number().nullish(),
  label: z.string().nullish(),
});
export type ExperimentMetricsResponseType = z.infer<
  typeof ExperimentMetricsSchema
>;

const MetricsWithStatSigSchema = MetricsSchema.extend({
  conversion_best: z.number().nullish(),
  conversion_beat_control: z.number().nullish(),
  average_order_value_best: z.number().nullish(),
  average_order_value_beat_control: z.number().nullish(),
  value_per_visitor_best: z.number().nullish(),
  value_per_visitor_beat_control: z.number().nullish(),
});

const AovHistogramMetricsSchema = MetricsSchema.extend({
  AOV_BUCKET: z.number().nullish(),
  AOV_BUCKET_COUNT: z.number().int().nullish(),
});

const CartProgressionMetricsSchema = MetricsSchema.extend({
  VISITORS: z.number().int().nullish(),
  ATC_RATE: z.number().nullish(),
  BEGIN_CHECKOUT_RATE: z.number().nullish(),
  CVR: z.number().nullish(),
});

const FreeShippingMetricsSchema = MetricsSchema.extend({
  PCT_ORDERS_FREE_SHIPPING: z.number().optional().nullish(),
});

const OrdersOverTimeMetricsSchema = MetricsSchema.extend({
  EVENT_TIME: z.string().nullish(),
  DAY_ORDERS: z.number().int().nullish(),
});

const PageViewsMetricsSchema = MetricsSchema.extend({
  VISITORS: z.number().int().nullish(),
  PAGE_VIEWS_PER_VISITOR: z.number().nullish(),
  PCT_COLLECTION_VIEWS: z.number().nullish(),
  PCT_PRODUCT_VIEWS: z.number().nullish(),
});

const OverviewMetricsModel = MetricsWithStatSigSchema.extend({
  VISITORS: z.number().int().nullish(),
  ORDERS: z.number().int().nullish(),
  TOTAL_PRODUCT_REVENUE: z.number().nullish(),
  TOTAL_PRODUCT_COGS: z.number().nullish(),
  TOTAL_PRODUCT_PROFIT: z.number().nullish(),
  TOTAL_PRODUCT_MARGIN: z.number().nullish(),
  TOTAL_SHIPPING_REVENUE: z.number().nullish(),
  TOTAL_SHIPPING_COST: z.number().nullish(),
  TOTAL_SHIPPING_PROFIT: z.number().nullish(),
  TOTAL_SHIPPING_MARGIN: z.number().nullish(),
  TRANSACTION_FEE: z.number().nullish(),
  TOTAL_REVENUE: z.number().nullish(),
  TOTAL_COST: z.number().nullish(),
  TOTAL_PROFIT: z.number().nullish(),
  TOTAL_MARGIN: z.number().nullish(),
  PROFIT_PER_VISITOR: z.number().nullish(),
  PROFIT_PER_ORDER: z.number().nullish(),
  PRODUCT_REVENUE_WITHOUT_COGS: z.number().nullish(),
  CVR: z.number().nullish(),
  REV_PER_VISITOR: z.number().nullish(),
  AVG_PRODUCT_REVENUE_PER_ORDER: z.number().nullish(),
  AVG_SHIPPING_REVENUE_PER_ORDER: z.number().nullish(),
  AOV: z.number().nullish(),
  AVG_UNITS_PER_ORDER: z.number().nullish(),
  AVG_PRODUCT_REV_PER_UNIT: z.number().nullish(),
  ATC_RATE: z.number().nullish(),
  BEGIN_CHECKOUT_RATE: z.number().nullish(),
});
export type OverviewMetricsModelType = z.infer<typeof OverviewMetricsModel>;

const ShippingMethodMetricsSchema = MetricsSchema.extend({
  SHIPPING_METHOD: z.string().nullish(),
  ORDERS_BY_RATE_NAME: z.number().int().nullish(),
  AVG_SHIPPING_REVENUE_BY_RATE_NAME: z.number().nullish(),
});

const ViewsOverTimeMetricsSchema = MetricsSchema.extend({
  EVENT_TIME: z.string().nullish(),
  DAY_VIEWS: z.number().nullish(),
});

const CampaignTotalsMetricsSchema = MetricsSchema.extend({
  AOV: z.number().nullish(),
  ATTACHMENT_RATE: z.number().nullish(),
  AVG_DISCOUNT: z.number().nullish(),
  EXPERIENCE_ID: z.string().uuid().nullish(),
  DISCOUNT_PCT_PROFIT: z.number().nullish(),
  DISCOUNT_PCT_REVENUE: z.number().nullish(),
  ORDERS: z.number().nullish(),
  ORDERS_DISCOUNTED: z.number().nullish(),
  PCT_ORDERS_DISCOUNTED: z.number().nullish(),
  REDEMPTION_RATE: z.number().nullish(),
  TOTAL_COGS: z.number().nullish(),
  TOTAL_DISCOUNT: z.number().nullish(),
  TOTAL_PROFIT: z.number().nullish(),
  TOTAL_REVENUE: z.number().nullish(),
  VISITORS: z.number().nullish(),
});
const CampaignOverTimeMetricsSchema = CampaignTotalsMetricsSchema.extend({
  EVENT_TIME: z.any().nullish(),
});

const DiscountsMetricSchema = MetricsSchema.extend({
  AVG_DISCOUNT_PER_ORDER: z.number().nullish(),
  AVG_DISCOUNT_PER_ORDER_ALL: z.number().nullish(),
  AVG_DISCOUNT_PER_ORDER_FILTERED: z.number().nullish(),
  DISCOUNT_PCT_FILTERED_AOV: z.number().nullish(),
  DISCOUNT_PCT_FILTERED_PPO: z.number().nullish(),
  DISCOUNT_PCT_TOTAL_AOV: z.number().nullish(),
  DISCOUNT_PCT_TOTAL_PPO: z.number().nullish(),
  FILTERED_NUM_ORDERS: z.number().nullish(),
  PCT_TOTAL_ORDERS: z.number().nullish(),
  TOTAL_PRODUCT_REVENUE: z.number().nullish(),
  PRODUCT_REVENUE_WITHOUT_COGS: z.number().nullish(),
});

const DiscountHistogramMetricSchema = MetricsSchema.extend({
  DISCOUNT_BUCKET: z.number().nullish(),
  DISCOUNT_BUCKET_COUNT: z.number().nullish(),
});

const ProductGroupsMetricSchema = MetricsSchema.extend({
  AVG_PROFIT_PER_UNIT: z.number().nullish(),
  AVG_REV_PER_UNIT: z.number().nullish(),
  AVG_UNITS_ORDERED: z.number().nullish(),
  NUM_UNITS: z.number().nullish(),
  PCT_TEST_GROUP_REVENUE: z.number().nullish(),
  PCT_TEST_GROUP_UNITS: z.number().nullish(),
  PRODUCT_GROUP: z.string().nullish(),
  PROFIT_PER_VISITOR: z.number().nullish(),
  REVENUE_PER_VISITOR: z.number().nullish(),
  UNITS_PER_VISITOR: z.number().nullish(),
  TOTAL_PRODUCT_REVENUE: z.number().nullish(),
  PRODUCT_REVENUE_WITHOUT_COGS: z.number().nullish(),
});

const UnitQuantityCompositionMetricSchema = MetricsSchema.extend({
  NUM_ORDERS: z.number().nullish(),
  PCT_TEST_GROUP_ORDERS: z.number().nullish(),
  PCT_TEST_GROUP_REVENUE: z.number().nullish(),
  UNIT_BUCKET: z.string().nullish(),
});

const UnitQuantityHistogramMetricSchema = MetricsSchema.extend({
  UNIT_QUANTITY_BUCKET: z.number().nullish(),
  UNIT_QUANTITY_BUCKET_COUNT: z.number().nullish(),
});

const NewReturningCustomersMetricSchema = MetricsSchema.extend({
  AOV: z.number().nullish(),
  CUSTOMER_TYPE: z.string().nullish(),
  NUM_ORDERS: z.number().nullish(),
  PCT_TEST_GROUP_ORDERS: z.number().nullish(),
});

const CustomEventGoalsMetricSchema = MetricsSchema.extend({
  PCT_VISITORS: z.number().nullish(),
  N_VISITORS: z.number().nullish(),
  EVENT_NAME: z.string().nullish(),
});
const CountryCodeMetricSchema = MetricsSchema.extend({
  C: z.number().nullish(),
  COUNTRY_CODE: z.string().nullish(),
});

const CustomEventsMetricSchema = MetricsSchema.extend({
  EVENT_NAME: z.string().nullish(),
});

const SubscriptionsMetricSchema = MetricsSchema.extend({
  TOTAL_ORDERS: z.number().nullish(),
  SUBSCRIPTION_ORDERS: z.number().nullish(),
  SUBSCRIPTION_PRODUCT_REVENUE: z.number().nullish(),
  SUBSCRIPTION_SHIPPING_REVENUE: z.number().nullish(),
  SUBSCRIPTION_REVENUE: z.number().nullish(),
  SUBSCRIPTION_PCT: z.number().nullish(),
  SUBSCRIPTION_AVG_PRODUCT_REVENUE_PER_ORDER: z.number().nullish(),
  SUBSCRIPTION_AVG_SHIPPING_REVENUE_PER_ORDER: z.number().nullish(),
  SUBSCRIPTION_AOV: z.number().nullish(),
  SUBSCRIPTION_AVG_UNITS_PER_ORDER: z.number().nullish(),
  SUBSCRIPTION_AVG_PRODUCT_REV_PER_UNIT: z.number().nullish(),
  SUBSCRIPTION_ORDERS_PER_VISITOR: z.number().nullish(),
  SUBSCRIPTION_REVENUE_PER_VISITOR: z.number().nullish(),
  SUBSCRIPTION_PROFIT_PER_VISITOR: z.number().nullish(),
  SUBSCRIPTION_COGS: z.number().nullish(),
  VISITORS: z.number().nullish(),
  SUBSCRIPTION_SHIPPING_COST: z.number().nullish(),
  SUBSCRIPTION_TRANSACTION_FEE: z.number().nullish(),
  SUBSCRIPTION_PROFIT: z.number().nullish(),
});

const AlwaysOnIntervalMetricSchema = MetricsSchema.extend({
  PERIOD_START_DT_LOCAL: z.string().nullish(),
  VISITORS: z.number().nullish(),
  ORDERS_WEB: z.number().nullish(),
  ORDERS_NON_WEB: z.number().nullish(),
  NET_REVENUE_WEB: z.number().nullish(),
  NET_REVENUE_NON_WEB: z.number().nullish(),
  ORDERS_WITH_IGID_WEB: z.number().nullish(),
  COGS_WEB: z.number().nullish(),
  COGS_NON_WEB: z.number().nullish(),
  AOV_WEB: z.number().nullish(),
  AOV_NON_WEB: z.number().nullish(),
  REVENUE_PER_VISITOR: z.number().nullish(),
  PCT_WEB_ORDERS_WITH_IGID: z.number().nullish(),
  TOTAL_SHIPPING_COST_WEB: z.number().nullish(),
  TOTAL_SHIPPING_COST_NON_WEB: z.number().nullish(),
  TRANSACTION_FEE_WEB: z.number().nullish(),
  TRANSACTION_FEE_NON_WEB: z.number().nullish(),
  TOTAL_COST_WEB: z.number().nullish(),
  TOTAL_COST_NON_WEB: z.number().nullish(),
  TOTAL_PROFIT_WEB: z.number().nullish(),
  TOTAL_PROFIT_NON_WEB: z.number().nullish(),
  PROFIT_PER_VISITOR: z.number().nullish(),
  CONVERSION_RATE: z.number().nullish(),
});

const DataReconciliationsMetricSchema = MetricsSchema.extend({
  EXCLUSION_REASON_N: z.string().nullish(),
  EXCLUSION_REASON_DESCRIPTION: z.string().nullish(),
  EXCLUSION_REASON_GROUP: z.string().nullish(),
  N_ORDERS: z.number().nullish(),
  ORDER_IDS: z.array(z.string()).nullish(),
  ORDER_NAMES: z.array(z.string()).nullish(),
});

const AlwaysOnCumulativeMetricSchema = MetricsSchema.extend({
  VISITORS: z.number().nullish(),
  N_ORDERS_WEB: z.number().nullish(),
  NET_REVENUE_WEB: z.number().nullish(),
  N_ORDERS_NON_WEB: z.number().nullish(),
  NET_REVENUE_NON_WEB: z.number().nullish(),
  N_ORDERS_WITH_IGID_WEB: z.number().nullish(),
  AOV_WEB: z.number().nullish(),
  AOV_NON_WEB: z.number().nullish(),
  TOTAL_CONVERSION_PCT: z.number().nullish(),
  TOTAL_PROFIT: z.number().nullish(),
  TOTAL_PROFIT_WEB: z.number().nullish(),
  TOTAL_PROFIT_NON_WEB: z.number().nullish(),
});

const CumulativeRevenueProfitOverTimeMetricSchema = MetricsSchema.extend({
  EVENT_TIME: z.string().nullish(),
  ORDERS: z.number().nullish(),
  VISITORS: z.number().nullish(),
  NET_REVENUE: z.number().nullish(),
  TOTAL_COGS: z.number().nullish(),
  AOV: z.number().nullish(),
  CONVERSION: z.number().nullish(),
  REVENUE_PER_VISITOR: z.number().nullish(),
  TOTAL_SHIPPING_COST: z.number().nullish(),
  TRANSACTION_FEE: z.number().nullish(),
  TOTAL_PROFIT: z.number().nullish(),
  PROFIT_PER_VISITOR: z.number().nullish(),
});

export const DatasetSchema = z.object({
  aovHistogram: AovHistogramMetricsSchema.strict().array().default([]),
  cartProgression: CartProgressionMetricsSchema.strict().array().default([]),
  freeShipping: FreeShippingMetricsSchema.strict().array().default([]),
  productGroups: ProductGroupsMetricSchema.strict().array().default([]),
  discounts: DiscountsMetricSchema.strict().array().default([]),
  discountHistogram: DiscountHistogramMetricSchema.strict().array().default([]),
  unitQuantityHistogram: UnitQuantityHistogramMetricSchema.strict()
    .array()
    .default([]),
  unitQuantityComposition: UnitQuantityCompositionMetricSchema.strict()
    .array()
    .default([]),
  newReturningCustomers: NewReturningCustomersMetricSchema.strict()
    .array()
    .default([]),
  ordersOverTime: OrdersOverTimeMetricsSchema.strict().array().default([]),
  pageViews: PageViewsMetricsSchema.strict().array().default([]),
  profit: OverviewMetricsModel.strict().array().default([]),
  shippingMethod: ShippingMethodMetricsSchema.strict().array().default([]),
  viewsOverTime: ViewsOverTimeMetricsSchema.strict().array().default([]),
  campaignTotals: CampaignTotalsMetricsSchema.strict().array().default([]),
  campaignOverTime: CampaignOverTimeMetricsSchema.strict().array().default([]),
  cumulativeRevenueProfitOverTime: z.any(),
  singleCampaignTotal: CampaignTotalsMetricsSchema.strict().array().default([]),
  customEventGoals: CustomEventGoalsMetricSchema.strict().array().default([]),
  customEventOptions: CustomEventsMetricSchema.strict().array().default([]),
  countryCodeOptions: CountryCodeMetricSchema.strict().array().default([]),
  allExperimentsOrdersVisitors: z.any(),
  subscriptions: SubscriptionsMetricSchema.strict().array().default([]),
  // alwaysOnCumulative: AlwaysOnCumulativeMetricSchema.strict()
  //   .array()
  //   .default([]),
  alwaysOnCumulative: z.any(),
  ordersReconciliation: z.any(),
  alwaysOnInterval: AlwaysOnIntervalMetricSchema.strict().array().default([]),
  compareDeviceType: z.any(),
  compareChannel: z.any(),
  compareNewReturning: z.any(),
});

export const DatasetMap: Record<DatasetId, ZodSchema> = {
  aovHistogram: AovHistogramMetricsSchema.strict().array().default([]),
  cartProgression: CartProgressionMetricsSchema.strict().array().default([]),
  freeShipping: FreeShippingMetricsSchema.strict().array().default([]),
  productGroups: ProductGroupsMetricSchema.strict().array().default([]),
  discounts: DiscountsMetricSchema.strict().array().default([]),
  discountHistogram: DiscountHistogramMetricSchema.strict().array().default([]),
  unitQuantityHistogram: UnitQuantityHistogramMetricSchema.strict()
    .array()
    .default([]),
  unitQuantityComposition: UnitQuantityCompositionMetricSchema.strict()
    .array()
    .default([]),
  newReturningCustomers: NewReturningCustomersMetricSchema.strict()
    .array()
    .default([]),
  ordersOverTime: OrdersOverTimeMetricsSchema.strict().array().default([]),
  pageViews: PageViewsMetricsSchema.strict().array().default([]),
  overview: OverviewMetricsModel.strict().array().default([]),
  shippingMethod: ShippingMethodMetricsSchema.strict().array().default([]),
  viewsOverTime: ViewsOverTimeMetricsSchema.strict().array().default([]),
  ordersExport: z.any(),
  lineItemsExport: z.any(),
  ordersReconciliationExport: z.any(),
  campaignTotals: CampaignTotalsMetricsSchema.strict().array().default([]),
  campaignOverTime: CampaignOverTimeMetricsSchema.strict().array().default([]),
  // cumulativeRevenueProfitOverTime:
  //   CumulativeRevenueProfitOverTimeMetricSchema.strict().array().default([]),
  cumulativeRevenueProfitOverTime: z.any(),
  singleCampaignTotal: CampaignTotalsMetricsSchema.strict().array().default([]),
  customEventGoals: CustomEventGoalsMetricSchema.strict().array().default([]),
  customEventOptions: CustomEventsMetricSchema.strict().array().default([]),
  countryCodeOptions: CountryCodeMetricSchema.strict().array().default([]),
  allExperimentsOrdersVisitors: z.any(),
  subscriptions: SubscriptionsMetricSchema.strict().array().default([]),
  // alwaysOnCumulative: AlwaysOnCumulativeMetricSchema.strict()
  //   .array()
  //   .default([]),
  alwaysOnCumulative: z.any(),
  ordersReconciliation: z.any(),
  alwaysOnInterval: AlwaysOnIntervalMetricSchema.strict().array().default([]),
  aiProfit: z.any(),
  compareDeviceType: z.any(),
  compareChannel: z.any(),
  compareNewReturning: z.any(),
};

const LossProbabilitiesSchema = z.object({
  VARIATION_NAME: z.string(),
  tolerance: z.number(),
  value_per_visitor_best: z.number(),
  value_per_visitor_beat_control: z.number(),
});

const ProbabilitiesResponseSchema = z.object({
  pointStatistics: MetricsWithStatSigSchema.array(),
  loss: LossProbabilitiesSchema.array(),
});

export type ProbabilitiesResponseType = z.infer<
  typeof ProbabilitiesResponseSchema
>;
